<template>
  <div class="cfg-page p-4">
    <h2 class="cfg-title">Конфигурация</h2>
    <p class="cfg-subtitle">Максимальные месячные лимиты инвестиций по классификациям клиентов.
      Ручной лимит, задаваемый MLRO при одобрении реклассификации, не может превышать значение соответствующей категории.</p>

    <b-card class="cfg-card" body-class="p-0">
      <div v-if="loading" class="cfg-loading"><b-spinner small class="mr-2" /> Загрузка…</div>
      <template v-else>
        <div class="cfg-row cfg-row--head">
          <div class="cfg-c-name">Классификация</div>
          <div class="cfg-c-limit">Максимальный лимит, $ / мес</div>
          <div class="cfg-c-action"></div>
        </div>
        <div v-for="row in rows" :key="row.category" class="cfg-row">
          <div class="cfg-c-name">
            <div class="cfg-cat">{{ categoryLabel(row.category) }}</div>
            <div class="cfg-code">{{ row.category }}</div>
          </div>
          <div class="cfg-c-limit">
            <b-form-input
              v-model.number="row.value"
              type="number"
              min="0"
              step="1000"
              :state="rowInvalid(row) ? false : null"
              class="cfg-input"
            />
          </div>
          <div class="cfg-c-action">
            <b-button
              size="sm"
              variant="warning"
              :disabled="row.saving || rowInvalid(row) || !rowChanged(row)"
              @click="save(row)"
            >
              <b-spinner v-if="row.saving" small class="mr-1" />
              Сохранить
            </b-button>
          </div>
        </div>
      </template>
    </b-card>

    <h3 class="cfg-log-title">Журнал изменений</h3>
    <b-card class="cfg-card" body-class="p-0">
      <div v-if="logLoading" class="cfg-loading"><b-spinner small class="mr-2" /> Загрузка…</div>
      <div v-else-if="!logRows.length" class="cfg-empty">Изменений пока не было.</div>
      <template v-else>
        <div class="cfg-log-row cfg-log-row--head">
          <div>Классификация</div>
          <div>Изменение, $ / мес</div>
          <div>Кто изменил</div>
          <div>Когда</div>
        </div>
        <div v-for="e in logRows" :key="e.id" class="cfg-log-row">
          <div class="cfg-log-cat">{{ categoryLabel(e.category) }}</div>
          <div class="cfg-log-change">
            <template v-if="e.oldLimit != null">
              <span class="cfg-log-old">{{ money(e.oldLimit) }}</span>
              <span class="cfg-log-arr">→</span>
            </template>
            <span class="cfg-log-new">{{ money(e.newLimit) }}</span>
          </div>
          <div class="cfg-log-who">{{ e.changedByName || e.changedByEmail || '—' }}</div>
          <div class="cfg-log-when">{{ formatDate(e.changedAt) }}</div>
        </div>
      </template>
    </b-card>

    <h3 class="cfg-log-title">Напоминания о зависших операциях</h3>
    <p class="cfg-subtitle">Фоновые Telegram-напоминания о пополнениях в статусе
      «ожидает», ордерах в стадии расчёта и ордерах, ждущих подтверждения курса.
      Повторяются с заданной периодичностью, пока операции не разгребут.</p>
    <b-card class="cfg-card">
      <div v-if="stuckLoading" class="cfg-loading"><b-spinner small class="mr-2" /> Загрузка…</div>
      <template v-else>
        <b-form-checkbox v-model="stuck.enabled" switch class="mb-3">Напоминания включены</b-form-checkbox>
        <div class="cfg-stuck-grid">
          <div class="cfg-stuck-field">
            <label>Порог пополнения, мин</label>
            <b-form-input v-model.number="stuck.depositMinutes" type="number" min="0" step="5" />
          </div>
          <div class="cfg-stuck-field">
            <label>Порог ордера, мин</label>
            <b-form-input v-model.number="stuck.orderMinutes" type="number" min="0" step="5" />
          </div>
          <div class="cfg-stuck-field">
            <label>Период напоминаний, мин</label>
            <b-form-input v-model.number="stuck.intervalMinutes" type="number" min="1" step="5" />
          </div>
        </div>
        <b-button
          size="sm" variant="warning" class="mt-3"
          :disabled="stuckSaving || stuckInvalid || !stuckChanged"
          @click="saveStuck"
        >
          <b-spinner v-if="stuckSaving" small class="mr-1" /> Сохранить
        </b-button>
      </template>
    </b-card>
  </div>
</template>

<script>
import { apiDomain, getHeader } from '@/config/config'
import axios from 'axios'

const CATEGORY_LABELS = {
  RETAIL_NQ: 'Розничный (неквалифицированный)',
  RETAIL_Q: 'Квалифицированный клиент',
  PROFESSIONAL: 'Профессиональный клиент',
}
// Порядок отображения.
const ORDER = ['RETAIL_NQ', 'RETAIL_Q', 'PROFESSIONAL']

export default {
  name: 'Configuration',
  data() {
    return {
      loading: true,
      rows: [], // { category, value, original, saving }
      logLoading: true,
      logRows: [],
      // Настройки напоминаний о зависших операциях.
      stuckLoading: true,
      stuckSaving: false,
      stuck: { enabled: true, depositMinutes: 60, orderMinutes: 15, intervalMinutes: 60 },
      stuckOrig: {},
    }
  },
  created() {
    this.load()
    this.loadLog()
    this.loadStuck()
  },
  computed: {
    stuckInvalid() {
      const s = this.stuck
      return !Number.isFinite(Number(s.depositMinutes)) || Number(s.depositMinutes) < 0
        || !Number.isFinite(Number(s.orderMinutes)) || Number(s.orderMinutes) < 0
        || !Number.isFinite(Number(s.intervalMinutes)) || Number(s.intervalMinutes) < 1
    },
    stuckChanged() {
      return JSON.stringify(this.stuck) !== JSON.stringify(this.stuckOrig)
    },
  },
  methods: {
    categoryLabel(c) { return CATEGORY_LABELS[c] || c },
    loadStuck() {
      this.stuckLoading = true
      axios.get(`${apiDomain}/admin/stuck-reminder-config`, { headers: getHeader() })
        .then(res => {
          const d = res.data || {}
          this.stuck = {
            enabled: !!d.enabled,
            depositMinutes: Number(d.depositMinutes) || 0,
            orderMinutes: Number(d.orderMinutes) || 0,
            intervalMinutes: Number(d.intervalMinutes) || 1,
          }
          this.stuckOrig = { ...this.stuck }
        })
        .catch(() => {
          this.$bvToast && this.$bvToast.toast('Не удалось загрузить настройки напоминаний.', {
            title: 'Ошибка', variant: 'danger', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4000,
          })
        })
        .finally(() => { this.stuckLoading = false })
    },
    saveStuck() {
      if (this.stuckInvalid || !this.stuckChanged) return
      this.stuckSaving = true
      axios.post(`${apiDomain}/admin/stuck-reminder-config`, {
        enabled: this.stuck.enabled,
        depositMinutes: Number(this.stuck.depositMinutes),
        orderMinutes: Number(this.stuck.orderMinutes),
        intervalMinutes: Number(this.stuck.intervalMinutes),
      }, { headers: getHeader() })
        .then(() => {
          this.stuckOrig = { ...this.stuck }
          this.$bvToast && this.$bvToast.toast('Настройки напоминаний сохранены.', {
            title: 'Сохранено', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500,
          })
        })
        .catch(err => {
          const sc = err && err.response && err.response.status
          this.$bvToast && this.$bvToast.toast(
            sc === 401 || sc === 403 ? 'Недостаточно прав.' : 'Не удалось сохранить настройки.',
            { title: 'Ошибка', variant: 'danger', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4500 }
          )
        })
        .finally(() => { this.stuckSaving = false })
    },
    money(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? n.toLocaleString('ru-RU') : String(v)
    },
    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      const p = n => String(n).padStart(2, '0')
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    loadLog() {
      this.logLoading = true
      axios.get(`${apiDomain}/admin/classification-limit-log`, { headers: getHeader() })
        .then(res => { this.logRows = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.logRows = [] })
        .finally(() => { this.logLoading = false })
    },
    rowChanged(row) { return Number(row.value) !== Number(row.original) },
    rowInvalid(row) {
      const v = row.value
      return v === '' || v === null || !Number.isFinite(Number(v)) || Number(v) < 0
    },
    load() {
      this.loading = true
      axios.get(`${apiDomain}/admin/classification-limits`, { headers: getHeader() })
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : []
          const byCat = {}
          list.forEach(l => { byCat[l.category] = l.maxLimit })
          this.rows = ORDER
            .filter(c => byCat[c] !== undefined)
            .map(c => ({ category: c, value: byCat[c], original: byCat[c], saving: false }))
        })
        .catch(() => {
          this.$bvToast && this.$bvToast.toast('Не удалось загрузить лимиты.', {
            title: 'Ошибка', variant: 'danger', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4000,
          })
          this.rows = []
        })
        .finally(() => { this.loading = false })
    },
    save(row) {
      if (this.rowInvalid(row) || !this.rowChanged(row)) return
      row.saving = true
      axios.post(
        `${apiDomain}/admin/classification-limits/${row.category}`,
        { maxLimit: Number(row.value) },
        { headers: getHeader() },
      )
        .then(() => {
          row.original = Number(row.value)
          this.$bvToast && this.$bvToast.toast(
            `Лимит «${this.categoryLabel(row.category)}» обновлён.`,
            { title: 'Сохранено', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500 }
          )
          this.loadLog()
        })
        .catch(err => {
          const sc = err && err.response && err.response.status
          this.$bvToast && this.$bvToast.toast(
            sc === 401 || sc === 403 ? 'Недостаточно прав.' : 'Не удалось сохранить лимит.',
            { title: 'Ошибка', variant: 'danger', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4500 }
          )
        })
        .finally(() => { row.saving = false })
    },
  },
}
</script>

<style scoped>
.cfg-page { max-width: 760px; }
.cfg-title { font-size: 22px; font-weight: 800; color: #111827; margin-bottom: 4px; }
.cfg-subtitle { color: #6B7280; font-size: 13px; line-height: 1.5; margin-bottom: 18px; max-width: 620px; }
.cfg-card { border: 1px solid #EAECF0; border-radius: 12px; overflow: hidden; }
.cfg-loading { padding: 24px; color: #6B7280; }
.cfg-row { display: grid; grid-template-columns: 1fr 220px 130px; align-items: center; gap: 12px; padding: 14px 16px; border-top: 1px solid #F1F3F5; }
.cfg-row:first-child { border-top: none; }
.cfg-row--head { background: #F9FAFB; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6B7280; }
.cfg-cat { font-weight: 600; color: #111827; font-size: 14px; }
.cfg-code { font-size: 11px; color: #9CA3AF; margin-top: 2px; }
.cfg-input { max-width: 200px; }
.cfg-c-action { text-align: right; }

.cfg-log-title { font-size: 15px; font-weight: 700; color: #111827; margin: 26px 0 10px; }
.cfg-empty { padding: 20px 16px; color: #9CA3AF; font-size: 13px; }
.cfg-log-row { display: grid; grid-template-columns: 1.4fr 1.4fr 1.4fr 1fr; align-items: center; gap: 12px; padding: 12px 16px; border-top: 1px solid #F1F3F5; font-size: 13px; }
.cfg-log-row:first-child { border-top: none; }
.cfg-log-row--head { background: #F9FAFB; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #6B7280; }
.cfg-log-cat { font-weight: 600; color: #111827; }
.cfg-log-old { color: #9CA3AF; text-decoration: line-through; }
.cfg-log-arr { color: #9CA3AF; margin: 0 6px; }
.cfg-log-new { color: #111827; font-weight: 700; }
.cfg-log-who { color: #374151; }
.cfg-log-when { color: #6B7280; }

.cfg-stuck-grid { display: grid; grid-template-columns: repeat(3, minmax(140px, 1fr)); gap: 14px; }
@media (max-width: 640px) { .cfg-stuck-grid { grid-template-columns: 1fr; } }
.cfg-stuck-field label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 4px; }
</style>
